import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { AddressApi } from "../api/AddressApi";
// import { log } from "./helpers";
import { ProductApi } from "../api/ProductApi";
import { RecordAMessageApi } from "../api/RecordAMessage";
import Config from "../Config";
import { useAppStore } from "./AppStore";
import { useCartStore } from "./CartStore";
import { safeStorage } from "./ssrStorage";

//Address Autocomplete

const AddressAutocomplete = async (address) => {
	if (address.length >= 4) {
		try {
			const { addresses } = await AddressApi.LookupPostcode(address);
			return { addresses };
		} catch (error) {
			return false;
		}
	}
};

type ItemSpecificState = {
	info: any;
	messageCardText: string;
	messageCardType: string;
	addressSuggestions: any;
	showAddressSuggestions: boolean;
	relatedProducts: any[];
	cartId: any;
	address: {
		address_name: string;
		address_line1: string;
		address_line2: string;
		address_town: string;
		address_county: string;
		address_postcode: string;
		address_country: string;
		address_phone: string;
		address_lat?: number;
		address_long?: number;
	};
	sizes: any[];
	selectedSize: string;
	selectedDeliveryOption: any;
	selectedAddons: any[];
	selectedDeliveryDate: Date;
	currentProductStep: number;
	printzwareId: any;
	personalisation: any;
	videoMsg: any;
	isEditing: boolean;
};

const itemSpecific: ItemSpecificState = {
	info: {},
	messageCardText: "",
	messageCardType: "",
	addressSuggestions: {},
	showAddressSuggestions: false,
	relatedProducts: [],
	cartId: false,
	address: {
		address_name: "",
		address_line1: "",
		address_line2: "",
		address_town: "",
		address_county: "",
		address_postcode: "",
		address_country: "",
		address_phone: "",
	},
	sizes: [],
	selectedDeliveryOption: null,
	selectedSize: "standard",
	selectedAddons: [],
	selectedDeliveryDate: new Date(),
	currentProductStep: 0,
	printzwareId: null,
	personalisation: null,
	videoMsg: null,
	isEditing: false,
};

type InitialProductState = {
	id: number | null;
	productsInfo: any[];
	deliveryOptions: any;
	productSteps: string[];
	availableAddons: any;
	price: number;
	originalPrice: number;
	msrpPrice: number;
	includedAddons: any;
	addonSort: any[];
};

const initialState: InitialProductState = {
	id: null,
	productsInfo: [],
	deliveryOptions: {},
	productSteps: ["Product Details", "Configure Your Gift"],
	availableAddons: {},
	price: 0,
	originalPrice: 0,
	msrpPrice: 0,
	includedAddons: {},
	addonSort: [],
};

interface ProductState extends ItemSpecificState, InitialProductState {
	GetInfo: (id: number, forceUpdate?: boolean, resetStore?: boolean) => Promise<any>;
	SetInfo: (info: any, forceUpdate?: boolean, resetStore?: boolean) => Promise<any>;
	GetProductInfo: (id: number, forceUpdate?: boolean) => Promise<any>;
	GetProductInfoBulk: (ids: number[], forceUpdate?: boolean) => Promise<any>;
	SetId: (id: number) => void;
	SetProductId: (productId: number) => void;
	ResetProductStore: () => void;
	GetSizes: () => void;
	GetRelatedProducts: (id: number) => Promise<void>;
	GetDeliveryMethods: (id: number, date: Date) => Promise<void>;
	SetDeliveryMethods: (deliveryOptions: any) => void;
	SetSelectedDeliveryMethod: (option: any) => void;
	UpdateProductDetails: (cart_id?: any) => Promise<boolean>;
	LoadCartProduct: (cartId: any) => Promise<any>;
	SetMessageCardText: (text: string) => void;
	SetMessageCardType: (type: string) => void;
	SetPrintzwareId: (id: any) => void;
	SetPersonalisation: (personalisation: any) => void;
	UploadVideoMsg: (recordingType: string, base64: string) => Promise<any>;
	SetVideoMsg: (videoMsg: string) => void;
	SetSelectedDeliveryDate: (option: any) => void;
	SetCurrentProductStep: (stepDirection: "next" | "prev" | number) => void;
	SetAddons: (addonId: number) => void;
	SetAvailableAddons: () => any;
	SetPrice: () => void;
	SetSize: (size: string) => void;
	AddToCart: ({ showCart }: { showCart?: boolean }) => Promise<any>;
	GetAddPersonalisedCardLink: () => string;
	SetAddedToCart: (id: any) => void;
	_computeSizes: (info: any) => { sizes: any[]; selectedSize: string };
	_computeAvailableAddons: (
		info: any,
		currentSelectedAddons?: any[],
	) => { availableAddons: any; includedAddons: any; addonSort: any[]; selectedAddons: any[] };
	_computePrice: (
		info: any,
		sizes: any[],
		selectedSize: string,
		selectedAddons: any[],
		availableAddons: any,
		deliveryOption: any,
		messageCardType: string,
		printzwareId: any,
		personalisation: any,
	) => { price: string; originalPrice: string; msrpPrice: string };
}

const useProductStore = create<ProductState>()(
	persist(
		devtools((set, get) => ({
			...itemSpecific,
			...initialState,
			async GetInfo(id, forceUpdate = false, resetStore = false) {
				if (resetStore === true) {
					get().ResetProductStore();
				}

				const info = await get().GetProductInfo(id, forceUpdate);

				// Batch all synchronous state updates together
				const { sizes, selectedSize } = get()._computeSizes(info);
				const { availableAddons, includedAddons, addonSort, selectedAddons } =
					get()._computeAvailableAddons(info);
				const { price, originalPrice, msrpPrice } = get()._computePrice(
					info,
					sizes,
					selectedSize,
					selectedAddons,
					availableAddons,
					null,
					"",
					null,
					null,
				);

				const selectedDeliveryDate =
					info.delivery.upcoming_dates && info.delivery.upcoming_dates.length > 0
						? new Date(info.delivery.upcoming_dates[0])
						: get().selectedDeliveryDate;

				set({
					info,
					id,
					sizes,
					selectedSize,
					availableAddons,
					includedAddons,
					addonSort,
					selectedAddons,
					price,
					originalPrice,
					msrpPrice,
					selectedDeliveryDate,
				});

				// Async operations run in parallel after state is set
				get().GetRelatedProducts(id);
				if (info.delivery.upcoming_dates && info.delivery.upcoming_dates.length > 0) {
					get().GetDeliveryMethods(id, new Date(info.delivery.upcoming_dates[0]));
				}

				return get().info;
			},
			async SetInfo(info, forceUpdate = false, resetStore = false) {
				if (resetStore === true) {
					get().ResetProductStore();
				}

				// Batch all synchronous state updates together
				const { sizes, selectedSize } = get()._computeSizes(info);
				const { availableAddons, includedAddons, addonSort, selectedAddons } =
					get()._computeAvailableAddons(info);
				const { price, originalPrice, msrpPrice } = get()._computePrice(
					info,
					sizes,
					selectedSize,
					selectedAddons,
					availableAddons,
					null,
					"",
					null,
					null,
				);

				const selectedDeliveryDate =
					info.delivery.upcoming_dates && info.delivery.upcoming_dates.length > 0
						? new Date(info.delivery.upcoming_dates[0])
						: get().selectedDeliveryDate;

				set({
					info,
					id: info.id,
					sizes,
					selectedSize,
					availableAddons,
					includedAddons,
					addonSort,
					selectedAddons,
					price,
					originalPrice,
					msrpPrice,
					selectedDeliveryDate,
				});

				// Async operations run in parallel after state is set
				get().GetRelatedProducts(info.id);
				if (info.delivery.upcoming_dates && info.delivery.upcoming_dates.length > 0) {
					get().GetDeliveryMethods(info.id, new Date(info.delivery.upcoming_dates[0]));
				}

				return get().info;
			},
			async GetProductInfo(id, forceUpdate = false) {
				if (!id) throw new Error("Invalid ID");

				const cachedProduct = get().productsInfo.find((product) => product.id === id);

				let cacheNeedsUpdate = false;
				if (cachedProduct && cachedProduct.cached_date) {
					const currentDate = new Date();
					const cachedDate = new Date(cachedProduct.cached_date);

					const difference = currentDate.getTime() - cachedDate.getTime();
					const minutesDifference = Math.floor(difference / 1000 / 60);

					if (minutesDifference >= Config.cacheTimes.product) {
						cacheNeedsUpdate = true;
					}
				} else {
					if (useAppStore.getState().isDebug) {
						console.log(`ProductStore.js - No cache date for product ${id}, forcing update`);
					}

					forceUpdate = true;
				}

				if (cachedProduct) {
					if (forceUpdate || cacheNeedsUpdate) {
						if (useAppStore.getState().isDebug) {
							console.log(`ProductStore.js - Cache update for product ${id}`);
						}
						const updatedProduct = await get().updateProductInfo(id);
						return updatedProduct;
					}

					return cachedProduct;
				}

				return get().updateProductInfo(id);
			},
			async GetProductInfoBulk(ids = [], forceUpdate = false) {
				if (!ids.length) throw new Error("Invalid IDs");

				const cachedProducts = ids.reduce(
					(acc, id) => {
						const cachedProduct = get().productsInfo.find((product) => product.id === id);
						let cacheNeedsUpdate = false;
						if (cachedProduct && cachedProduct.cached_date) {
							const currentDate = new Date();
							const cachedDate = new Date(cachedProduct.cached_date);

							const difference = currentDate.getTime() - cachedDate.getTime();
							const minutesDifference = Math.floor(difference / 1000 / 60);

							if (minutesDifference >= Config.cacheTimes.product) {
								cacheNeedsUpdate = true;
							}
						} else {
							if (useAppStore.getState().isDebug) {
								console.log(
									`ProductStore.js - No cache date for products ${ids.join(", ")}, forcing update`,
								);
							}
							forceUpdate = true;
						}

						if (cachedProduct) {
							if (forceUpdate || cacheNeedsUpdate) {
								acc["fetch"].push(id);
							} else {
								acc["cached"].push(id);
							}
						} else {
							acc["fetch"].push(id);
						}

						return acc;
					},
					{ cached: [], fetch: [] },
				);

				if (useAppStore.getState().isDebug) {
					console.log(`ProductStore.js - Fetching cached products`, cachedProducts);
				}

				if (cachedProducts["fetch"].length) {
					await get().updateProductInfoBulk(cachedProducts["fetch"]);
				}

				return get().productsInfo;
			},
			async updateProductInfo(id) {
				try {
					const response = await ProductApi.getInfo(id);
					const product = response.product;
					product.cached_date = new Date();
					set((prev) => {
						const updatedProducts = prev.productsInfo.filter((product) => product.id !== id);
						return { productsInfo: [...updatedProducts, product] };
					});
					return product;
				} catch (error) {
					console.error("Error fetching product info:", error);
					throw error;
				}
			},
			async updateProductInfoBulk(ids = []) {
				try {
					const response = await ProductApi.getInfoBulk(ids);

					if (useAppStore.getState().isDebug) {
						console.log(`ProductStore.js - Updating product info for ${ids.join(", ")}`, response);
					}

					const products = response.products;
					const productsCache = Object.values(products).map((product) => {
						product.cached_date = new Date();
						return product;
					});
					set((prev) => {
						const updatedProducts = prev.productsInfo.filter((product) => !ids.includes(product.id));
						return { productsInfo: [...updatedProducts, ...productsCache] };
					});
					return productsCache;
				} catch (error) {
					console.error("Error fetching product info:", error);
					throw error;
				}
			},
			// Pure computation helpers (no state mutations)
			_computeSizes(info) {
				// Sizes are now normalized in ProductApi, so we just use them directly
				let sizes = info?.has_sizes && info.sizes ? [...info.sizes] : [];

				try {
					if (info?.disabled_sizes?.disabled_sizes) {
						const disabledSizes = Object.values(info.disabled_sizes.disabled_sizes);
						sizes = sizes
							.map((size, key) => {
								const disabled = disabledSizes[key] === true;
								return { ...size, enabled: !disabled };
							})
							.filter((size) => size.enabled === true);
					}
				} catch (error) {
					console.log(`ProductStore.js - Error running through disabled sizes.`, error);
				}

				const selectedSize = sizes.find((size) => size.enabled === true)?.size ?? "standard";
				return { sizes, selectedSize };
			},
			_computeAvailableAddons(info, currentSelectedAddons = []) {
				let availableAddons = {};
				let includedAddons = {};
				let selectedAddons = [...currentSelectedAddons];

				Object.keys(info.addons).forEach((addonType) => {
					if (addonType === "included") {
						info.addons[addonType].forEach((addon, key) => {
							info.addons[addonType][key]["price"] = "0";
							includedAddons[addon.id] = addon;
							if (!selectedAddons.includes(addon.id)) {
								selectedAddons = [...selectedAddons, addon.id];
							}
						});
					}
					info.addons[addonType].forEach((addon) => {
						availableAddons[addon.id] = addon;
					});
				});

				const addonSort = info.addons.other.map((addon) => addon.id);

				return { availableAddons, includedAddons, addonSort, selectedAddons };
			},
			_computePrice(
				info,
				sizes,
				selectedSize,
				selectedAddons,
				availableAddons,
				deliveryOption,
				messageCardType,
				printzwareId,
				personalisation,
			) {
				let price = parseFloat(info.price_retail);
				let originalPrice = parseFloat(info.price_retail);
				let msrpPrice = parseFloat(info.price_msrp);

				if (sizes && sizes.length > 0) {
					const sizeData = sizes.find((size) => size.size === selectedSize);
					if (sizeData) {
						price += parseFloat(sizeData.price);
						originalPrice = parseFloat(info.price_retail) + parseFloat(sizeData.price);
						msrpPrice = parseFloat(info.price_msrp) + parseFloat(sizeData.price);
					}
				}

				if (selectedAddons.length > 0 && availableAddons) {
					selectedAddons.forEach((addonId) => {
						if (availableAddons[addonId]) {
							price += parseFloat(availableAddons[addonId].price);
						}
					});
				}

				if (deliveryOption) {
					price += parseFloat(deliveryOption.cost);
				}

				if ((personalisation || printzwareId) && messageCardType === "personalised") {
					const cardPrice = personalisation?.price ?? (info.free_card ? 0 : Config.personalisedCardPrice);
					price += parseFloat(cardPrice);
				}

				return {
					price: price.toFixed(2),
					originalPrice: originalPrice.toFixed(2),
					msrpPrice: msrpPrice.toFixed(2),
				};
			},
			GetSizes() {
				let info = get().info;
				const { sizes, selectedSize } = get()._computeSizes(info);
				set({ sizes, selectedSize });
			},
			async GetRelatedProducts(id) {
				let { suggestions } = await ProductApi.getRelated(id);
				set({ relatedProducts: Object.values(suggestions) });
			},
			async GetDeliveryMethods(id, date) {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are zero-indexed
				const day = String(date.getDate()).padStart(2, "0");
				const formattedDateString = `${year}-${month}-${day}`;
				const deliveryOptions = await ProductApi.getDeliveryMethods(id, formattedDateString);

				// Batch delivery options and selected method in one update
				const selectedDeliveryOption = deliveryOptions[0] || null;
				const { price, originalPrice, msrpPrice } = get()._computePrice(
					get().info,
					get().sizes,
					get().selectedSize,
					get().selectedAddons,
					get().availableAddons,
					selectedDeliveryOption,
					get().messageCardType,
					get().printzwareId,
					get().personalisation,
				);

				set({ deliveryOptions, selectedDeliveryOption, price, originalPrice, msrpPrice });
				get().UpdateProductDetails();
			},
			async SetDeliveryMethods(deliveryOptions) {
				set({ deliveryOptions });
			},
			SetSelectedDeliveryMethod: (option) => {
				let deliveryOptions = get().deliveryOptions;
				if (deliveryOptions[option]) {
					set({ selectedDeliveryOption: deliveryOptions[option] });
				}
				get().SetPrice();
				get().UpdateProductDetails();
			},
			async UpdateProductDetails(cart_id = false) {
				const cartId = cart_id !== false ? cart_id : get().cartId;
				if (cartId === false) return;

				if (useAppStore.getState().isDebug) {
					console.log("ProductStore.js - Updating product details", cartId);
				}

				if (get().isEditing) {
					if (useCartStore.getState().cart[cartId]) {
						try {
							if (useAppStore.getState().isDebug) {
								console.log(`ProductStore.js - Updating product in cart with updated info`);
							}
							await useCartStore.getState().UpdateProductInCart(cartId, get().BuildProductInfo());
							return true;
						} catch {
							console.error(
								`ProductStore.js - Tried to update product with id ${cartId} but no cart id was set`,
							);
							return false;
						}
					} else {
						console.error("ProductStore.js - No cart id set, cannot update product details");
						return false;
					}
				} else {
					return false;
				}
			},
			async LoadCartProduct(cartId) {
				if (!cartId) return;

				const cart = useCartStore.getState().cart;
				if (!cart) return;

				const product = cart[cartId];
				if (!product) return;

				const deliveryDate = product.deliverydate ? new Date(product.deliverydate) : null;
				const recipientInfo = product.recipient_info ?? {};
				const selectedAddons = Array.isArray(product.addons)
					? product.addons.map((addon) => parseInt(addon.id))
					: [];

				const options = {
					selectedSize: product.size ?? null,
					selectedDeliveryDate: deliveryDate instanceof Date && !isNaN(deliveryDate) ? deliveryDate : null,
					selectedDeliveryOption: {
						name: product.deliverymethod ?? "",
					},
					messageCardType: product.personalisation || product.personalised ? "personalised" : "standard",
					messageCardText: product.messagecard ?? "",
					printzwareId: product.personalisation?.order_reference ?? product.personalised ?? null,
					personalisation: product.personalisation ?? null,
					address: {
						address_country: recipientInfo.country ?? "",
						address_county: recipientInfo.county ?? "",
						address_line1: recipientInfo.line1 ?? "",
						address_line2: recipientInfo.line2 ?? "",
						address_postcode: recipientInfo.postcode ?? "",
						address_town: recipientInfo.town ?? "",
						address_name: recipientInfo.name ?? "",
						address_phone: recipientInfo.phone ?? "",
					},
					selectedAddons: selectedAddons,
				};

				set({ ...options });

				return options;
			},
			SetMessageCardText: (text) => {
				set({ messageCardText: text });
				get().UpdateProductDetails();
			},
			SetMessageCardType: (type) => {
				set({ messageCardType: type });
				get().UpdateProductDetails();
			},
			SetPrintzwareId: (id) => {
				set({ printzwareId: id });
			},
			SetPersonalisation: (personalisation) => {
				set({ personalisation });
			},
			UploadVideoMsg: async (recordingType, base64) => {
				try {
					const { recording } = await RecordAMessageApi.uploadRecording(recordingType, base64);
					const { errors, location } = recording;
					if (errors.length) {
						throw new Error(errors);
					} else {
						set({ videoMsg: location });
					}

					return recording;
				} catch (error) {
					console.error(`Failed to upload recording: ${error}`);
				}
			},
			SetVideoMsg: (videoMsg) => {
				set({ videoMsg });
			},
			SetSelectedDeliveryDate: (option) => {
				const date = new Date(option);
				set({ selectedDeliveryDate: date });
				get().GetDeliveryMethods(get().id, date);
				get().UpdateProductDetails();
			},
			SetCurrentProductStep(stepDirection) {
				let currentStep = get().currentProductStep;
				if (stepDirection === "next") {
					currentStep++;
				} else if (stepDirection === "prev") {
					currentStep--;
				} else {
					currentStep = stepDirection;
				}
				set({ currentProductStep: currentStep });
			},
			SetAddons: (addonId) => {
				let addons = [];
				if (get().selectedAddons.includes(addonId)) {
					addons = get().selectedAddons.filter((item) => item !== addonId); //remove
				} else {
					addons = [...get().selectedAddons, addonId]; //add
				}
				set({ selectedAddons: addons });
				get().SetPrice();
				get().UpdateProductDetails();
			},
			SetAvailableAddons: () => {
				let info = get().info;
				const currentSelectedAddons = get().selectedAddons;
				const { availableAddons, includedAddons, addonSort, selectedAddons } = get()._computeAvailableAddons(
					info,
					currentSelectedAddons,
				);

				set({ availableAddons, includedAddons, addonSort, selectedAddons });
				return availableAddons;
			},
			SetPrice: () => {
				let info = get().info;
				let sizes = get().sizes;
				let selectedSize = get().selectedSize;
				let selectedAddons = get().selectedAddons;
				let availableAddons = get().availableAddons;
				let deliveryOption = get().selectedDeliveryOption;
				let messageCardType = get().messageCardType;
				let printzwareId = get().printzwareId;

				const { price, originalPrice, msrpPrice } = get()._computePrice(
					info,
					sizes,
					selectedSize,
					selectedAddons,
					availableAddons,
					deliveryOption,
					messageCardType,
					printzwareId,
					get().personalisation,
				);

				set({ price, originalPrice, msrpPrice });
			},
			SetSize: (size) => {
				set({ selectedSize: size });
				get().SetPrice();
				get().UpdateProductDetails();
			},
			SetId: (passedId) => {
				set({ id: passedId });

				get().SetPrice();
			},
			SetProductId: (productId) => set({ productId }),
			SetAddressSuggestions: (postcode) => {
				AddressAutocomplete(postcode).then((ret) => {
					if (ret.addresses !== undefined && ret.addresses.length > 0) {
						set({
							addressSuggestions: ret,
							showAddressSuggestions: true,
						});
					} else {
						set({
							addressSuggestions: [],
							showAddressSuggestions: false,
						});
					}
				});
			},
			SetAddress: (addressInformation) => {
				set({
					address: { ...get().address, ...addressInformation },
					showAddressSuggestions: false,
				});
				get().UpdateProductDetails();
			},
			async AddToCart({ showCart = true }) {
				let productInfo = get().BuildProductInfo();
				return useCartStore.getState().AddToCart(productInfo, showCart);
			},
			GetAddPersonalisedCardLink() {
				const product_id = get().id;
				return `${Config.apiUrl}/${Config.apiVersion}/products/${product_id}/add-personalised-card`;
			},

			ConvertAddonIds: () => {
				const addons = get().selectedAddons;

				if (addons.length === 0) return [];

				return addons.map((addon) => {
					return get().availableAddons[addon].id;
				});
			},
			BuildProductInfo: () => {
				const selectedDeliveryDate = get().selectedDeliveryDate ?? new Date();
				const address = get().address;
				const availableCountries = Object.values(get().info?.available_countries ?? {});
				const addressCountry = address.address_country || (availableCountries.length === 1 ? availableCountries[0] : "");
				let productInfo = {
					productid: get().id,
					attr_size: get().selectedSize ?? "",
					attr_addons: get().ConvertAddonIds(),
					attr_deliverydate:
						selectedDeliveryDate.getFullYear() +
						"-" +
						(selectedDeliveryDate.getMonth() + 1) +
						"-" +
						selectedDeliveryDate.getDate(),
					attr_deliverymethod: get().selectedDeliveryOption ? get().selectedDeliveryOption.name : "",
					attr_messagecard: get().messageCardText ?? "",
					attr_quantity: 1,
					...(get().personalisation
						? { attr_personalisation: get().personalisation }
						: get().printzwareId && { attr_personalised: get().printzwareId }),
					...(get().videoMsg && { attr_videomsg: get().videoMsg }),
					...address,
					...(addressCountry && { address_country: addressCountry }),
				};

				Object.keys(productInfo).forEach((value) => {
					//check if the values are empty
					if (productInfo[value] === "") {
						delete productInfo[value];
					}
				});

				if (useAppStore.getState().isDebug) {
					console.log(`ProductStore.js - Building product info`, productInfo);
				}

				return productInfo;
			},
			ResetProductStore: () => {
				if (useAppStore.getState().isDebug) {
					console.log(`ProductStore.js - Resetting product store`);
				}

				set(itemSpecific);
			},
			SetAddedToCart: (id) => {
				if (useAppStore.getState().isDebug) {
					console.log(`ProductStore.js - Added to cart, setting cart id to ${id}`);
				}

				set({ cartId: id, isEditing: true });
			},
		})),
		{
			name: "product-storage",
			storage: safeStorage,
		},
	),
);

export { useProductStore };
